import { FC } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { usePages } from '@rikstv/play-common/src/components/pages/usePages';
import { SkipLink } from '@rikstv/play-common/src/components/skipLink/SkipLink';
import { useIsAuthenticated } from '@rikstv/play-common/src/hooks/useIsAuthenticated';
import { UnstyledLink } from '@rikstv/play-common/src/router/Link';
import { commonRouterPaths } from '@rikstv/play-common/src/router/router.path';
import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';
import { StrimNavigation, UnstyledLinkProps } from '@rikstv/shared-strim-components';

import { selectNotifications } from '../../../domain/user/forces/selectors';
import { routerPaths } from '../../router/router.path';

const UnstyledNavigationLink: FC<UnstyledLinkProps> = props => (
  <UnstyledLink to={props.href} scrollToTopOnReclick {...props} />
);

/**
 * The underlying component is shared between Strim.Play and the CMS
 */

export const Navigation: FC = () => {
  const { pathname } = useLocation();
  const appNavigationItems = useAppNavigation();
  const isLoggedIn = useIsAuthenticated();
  const { notifications } = useSelector(selectNotifications(pathname));

  return (
    <>
      <SkipLink elementId="main" buttonText="Hopp til hovedinnholdet" />
      <StrimNavigation
        isLoggedIn={isLoggedIn}
        login={() => authService.login()}
        currentUrl={pathname}
        getAppNavigation={salesItems => {
          return showSalesItems(pathname) ? salesItems : appNavigationItems;
        }}
        linkComponent={UnstyledNavigationLink}
        accountNotifications={notifications.length}
      />
    </>
  );
};

const pathsWithSalesItems = [routerPaths.signUpPackageSelect, routerPaths.purchaseProducts];
const showSalesItems = (pathname: string) => {
  if (pathname === routerPaths.frontpage) {
    return true;
  }
  if (pathname === routerPaths.paymentMethodChange) {
    return false;
  }

  return pathsWithSalesItems.some(path => pathname.startsWith(path));
};

const tvGuideItem = { action: commonRouterPaths.tvGuide, children: 'TV-guide' };
const liveItem = { action: commonRouterPaths.watchLiveAssetWithoutId, children: 'Direkte' };
type AppNavigationItem = {
  children: string;
  action: string;
};

const useAppNavigation: () => AppNavigationItem[] = () => {
  const { pages = [] } = usePages();
  return pages
    .map(page => ({
      children: page.name,
      action: `/${page.slug}`,
    }))
    .concat(liveItem, tvGuideItem);
};
